import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CalendarClock, FileText, MessageSquare, ArrowRight, Phone, Mail } from "lucide-react";

export default function DashboardPage() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [docs, setDocs] = useState([]);
  const [msgs, setMsgs] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/appointments/me").then((r) => r.data.appointments).catch(() => []),
      api.get("/documents").then((r) => r.data.documents).catch(() => []),
      api.get("/messages").then((r) => r.data.messages).catch(() => []),
    ]).then(([a, d, m]) => { setAppts(a || []); setDocs(d || []); setMsgs(m || []); });
  }, []);

  const upcoming = (appts || []).filter((a) => a.preferred_date >= new Date().toISOString().slice(0, 10));
  const next = upcoming[0];

  return (
    <div data-testid="page-dashboard" className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-3">— Dashboard</div>
        <h1 className="font-serif text-5xl text-[#0F292F] font-light tracking-tight">
          Welcome back, <span className="italic text-[#8E6A2B]">{user?.name?.split(" ")[0] || "Friend"}</span>.
        </h1>
        <p className="mt-3 text-[#52525B] max-w-xl">
          Here's a snapshot of where things stand. Your advisor is one message away.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 bg-white border border-black/10 p-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-5">
            <CalendarClock size={14} /> Next Appointment
          </div>
          {next ? (
            <div data-testid="dashboard-next-appt">
              <h2 className="font-serif text-3xl text-[#0F292F] mb-2">{next.service}</h2>
              <p className="text-[#52525B]">
                {new Date(next.preferred_date).toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                {" · "}{next.preferred_time}
              </p>
              {next.notes && <p className="mt-4 text-sm text-[#52525B] italic">"{next.notes}"</p>}
              <div className="mt-6 inline-flex items-center gap-2 px-3 py-1 bg-[#8E6A2B]/15 text-[#0F292F] text-xs uppercase tracking-[0.2em]">
                {next.status}
              </div>
            </div>
          ) : (
            <div>
              <p className="text-[#52525B] mb-6">No upcoming appointments.</p>
              <Link to="/portal/appointments" className="btn-primary">Schedule one <ArrowRight size={16} /></Link>
            </div>
          )}
        </div>

        <div className="lg:col-span-4 bg-[#0F292F] text-white p-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#CFA968] mb-5">Your Tax Advisor</div>
          <h3 className="font-serif text-2xl mb-1">Harold Hodges, EA</h3>
          <p className="text-white/70 text-sm mb-6">Founder · Enrolled Agent</p>
          <div className="space-y-3 text-sm">
            <a href="tel:+15715985790" className="flex items-center gap-3 text-white hover:text-[#CFA968]">
              <Phone size={14} /> (571) 598-5790
            </a>
            <a href="mailto:helliotassociates@gmail.com" className="flex items-center gap-3 text-white hover:text-[#CFA968]">
              <Mail size={14} /> helliotassociates@gmail.com
            </a>
          </div>
          <div className="hairline my-6 bg-white/10" />
          <Link to="/portal/messages" data-testid="dashboard-message-cta" className="text-sm uppercase tracking-[0.2em] text-[#CFA968] he-link">
            Send a secure message →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/portal/documents" className="bg-white border border-black/10 p-6 hover:bg-[#F3F1EC] transition-colors group">
          <FileText size={20} strokeWidth={1.2} className="text-[#8E6A2B] mb-4" />
          <div className="font-serif text-2xl text-[#0F292F]">{docs.length}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">Documents on file</div>
          <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#0F292F] group-hover:text-[#8E6A2B] inline-flex items-center gap-2">
            Manage vault →
          </div>
        </Link>

        <Link to="/portal/messages" className="bg-white border border-black/10 p-6 hover:bg-[#F3F1EC] transition-colors group">
          <MessageSquare size={20} strokeWidth={1.2} className="text-[#8E6A2B] mb-4" />
          <div className="font-serif text-2xl text-[#0F292F]">{msgs.length}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">Messages</div>
          <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#0F292F] group-hover:text-[#8E6A2B] inline-flex items-center gap-2">
            Open inbox →
          </div>
        </Link>

        <Link to="/portal/appointments" className="bg-white border border-black/10 p-6 hover:bg-[#F3F1EC] transition-colors group">
          <CalendarClock size={20} strokeWidth={1.2} className="text-[#8E6A2B] mb-4" />
          <div className="font-serif text-2xl text-[#0F292F]">{appts.length}</div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mt-1">Appointments (history)</div>
          <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#0F292F] group-hover:text-[#8E6A2B] inline-flex items-center gap-2">
            View all →
          </div>
        </Link>
      </div>
    </div>
  );
}
